import type { ReactNode } from 'react'
import { ApiError } from '../api/client'

// Elementi di interfaccia condivisi. Pochi e generici di proposito: le schermate compongono
// questi, così l'aspetto resta coerente senza dover portarsi dietro una libreria.

export function Card({ title, actions, children }: { title?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-head">
          {title && <h2>{title}</h2>}
          {actions && <div className="card-actions">{actions}</div>}
        </header>
      )}
      <div className="card-body">{children}</div>
    </section>
  )
}

export function Button({
  children,
  onClick,
  variant = 'default',
  disabled,
  title,
  type = 'button',
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'default' | 'primary' | 'danger' | 'ghost'
  disabled?: boolean
  title?: string
  type?: 'button' | 'submit'
}) {
  return (
    <button type={type} className={`btn btn-${variant}`} onClick={onClick} disabled={disabled} title={title}>
      {children}
    </button>
  )
}

/**
 * Messaggio d'errore che distingue i casi. Un 503 dice "il gestionale non risponde, riprova":
 * è un'informazione diversa da un guasto, e l'utente deve poterla riconoscere subito.
 */
export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const api = error instanceof ApiError ? error : undefined
  const kind = api?.kind ?? 'error'

  return (
    <div className={`notice notice-${kind === 'unavailable' ? 'warn' : 'error'}`}>
      <strong>{api?.title ?? 'Errore'}</strong>
      {api?.detail && <p>{api.detail}</p>}
      {!api && <p>{String(error)}</p>}
      {onRetry && (
        <Button variant="ghost" onClick={onRetry}>
          Riprova
        </Button>
      )}
    </div>
  )
}

export function Spinner({ label = 'Caricamento…' }: { label?: string }) {
  return (
    <div className="spinner" role="status">
      <span className="spinner-dot" />
      {label}
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: BadgeTone }) {
  return <span className={`badge badge-${tone}`}>{children}</span>
}

export type BadgeTone = 'neutral' | 'info' | 'ok' | 'warn' | 'danger' | 'muted'

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  )
}

// `useGrouping: true` è esplicito di proposito: per impostazione predefinita i numeri di quattro
// cifre non vengono raggruppati, quindi un importo verrebbe scritto «3007,95 €» invece di
// «3.007,95 €». Su una colonna di importi la differenza si legge a colpo d'occhio.
const EURO = new Intl.NumberFormat('it-IT', {
  style: 'currency', currency: 'EUR', useGrouping: true,
})

export function Money({ value }: { value?: number | null }) {
  if (value === undefined || value === null) return <span className="muted">—</span>
  return <span className="num">{EURO.format(value)}</span>
}

export function Num({ value, decimals = 0 }: { value?: number | null; decimals?: number }) {
  if (value === undefined || value === null) return <span className="muted">—</span>
  return (
    <span className="num">
      {value.toLocaleString('it-IT', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: true,
      })}
    </span>
  )
}

/** Concordanza singolare/plurale: «1 punto vendita», «3 punti vendita». */
export function plural(count: number, singular: string, plural_: string) {
  return `${count} ${count === 1 ? singular : plural_}`
}

/** Data in forma breve italiana. Le date arrivano dall'API come 'yyyy-MM-dd'. */
export function formatDate(iso?: string | null) {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

export function formatDateTime(iso?: string | null) {
  if (!iso) return '—'
  const dt = new Date(iso)
  return dt.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
}

/** Lunedì della settimana che contiene la data indicata. */
export function startOfWeek(date: Date) {
  const copy = new Date(date)
  const day = (copy.getDay() + 6) % 7
  copy.setDate(copy.getDate() - day)
  copy.setHours(0, 0, 0, 0)
  return copy
}

export function toIsoDate(date: Date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
